'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface OrderStep {
  number: number;
  title: string;
  description: string;
  icon: string;
}

interface OrderFlowProps {
  steps: OrderStep[];
}

export default function OrderFlow({ steps }: OrderFlowProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section className="py-20 bg-gradient-to-br from-accent/5 via-background to-primary/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-heading font-extrabold text-primary mb-4">
            Simple Ordering Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Get your ParkPing Card delivered to your doorstep in just a few clicks
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {/* Connecting line */}
            {steps.length > 1 && (
              <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-accent via-accent/70 to-transparent pointer-events-none" />
            )}

            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Arrow connector for last items */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-20 left-full w-full h-0.5 bg-gradient-to-r from-accent to-accent/0 -translate-x-1/2 z-0" />
                )}

                <div className="relative bg-card rounded-2xl p-6 shadow-subtle hover:shadow-card transition-all duration-300 hover:-translate-y-2 z-10 border border-border hover:border-accent/25">
                  <div className="flex flex-col items-center text-center space-y-5">
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-accent to-accent/70 rounded-full flex items-center justify-center shadow-cta">
                        <Icon name={step.icon as any} size={36} className="text-white" />
                      </div>
                      <div className="absolute -top-3 -right-3 w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-extrabold text-lg shadow-subtle">
                        {step.number}
                      </div>
                    </div>
                    <h3 className="text-xl font-heading font-extrabold text-foreground">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link
              href="/get-started"
              className="inline-flex items-center justify-center px-10 py-5 bg-accent text-accent-foreground font-heading font-extrabold text-lg rounded-xl shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 hover:brightness-105 transition-all duration-300"
            >
              Start your order
            </Link>
            <p className="mt-4 text-sm text-muted-foreground font-medium">
              One-time card cost applies on first purchase.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
